import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import LoadingLayout from 'components/Loading';
import { ROLES } from 'helpers/enum';
import { checkAuthenticationAdmin, checkAuthenticationSuperAdmin } from 'helpers/lib';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { buildNavGroups, ShellRole } from './navConfig';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import Notification from '../../containers/Notification';
import User from '../../containers/User';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { NotificationService } from '../../services/notification/notification.service';

const SIDEBAR_STORAGE_KEY = 'amatrium.sidebar.collapsed';

interface AppShellProps {
  /** which role's nav to render */
  role: ShellRole;
  isLoading?: boolean;
  children?: React.ReactNode;
}

/** Resolve the page title + crumb from the active route label (i18n key). */
const useActiveRouteMeta = (role: ShellRole) => {
  const { t } = useTranslation();
  const location = useLocation();
  const groups = buildNavGroups(role);
  for (const g of groups) {
    const item = g.items.find((i) => i.path === location.pathname);
    if (item) return { title: t(item.label), crumb: t(g.labelKey), groups };
  }
  return { title: '', crumb: '', groups };
};

const AppShell = ({ role, isLoading = false, children }: AppShellProps) => {
  const { clearNewNotificationFlag, hasNewNotification } = useNotificationContext();

  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState({ all: 0, unread: 0 });

  const { title, crumb, groups } = useActiveRouteMeta(role);

  // Mobile drawer nav: member (user role) sees only Chat on phones (matches production);
  // admin/superadmin keep Chat + Summary. (#77945, #78881)
  const mobileToolKeys = role === 'user' ? ['chat'] : ['chat', 'summary'];
  const mobileGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => mobileToolKeys.includes(i.key)) }))
    .filter((g) => g.items.length > 0);

  const isAdmin =
    checkAuthenticationAdmin() === ROLES.ADMIN ||
    checkAuthenticationSuperAdmin() === ROLES.SUPER_ADMIN;

  const drawerBg = useColorModeValue('#ffffff', '#121419');

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const loadNotificationCount = useCallback(async () => {
    try {
      const count = await NotificationService.getNotificationCount();
      setNotificationCount(count);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  }, []);

  useEffect(() => {
    loadNotificationCount();
  }, [loadNotificationCount]);

  useEffect(() => {
    if (hasNewNotification) {
      loadNotificationCount();
      clearNewNotificationFlag();
    }
  }, [hasNewNotification, loadNotificationCount, clearNewNotificationFlag]);

  // Sidebar "Usage & members" nav item opens the admin's own-org modal (no route).
  useEffect(() => {
    const open = () => setCompanyOpen(true);
    window.addEventListener('openMyCompany', open);
    return () => window.removeEventListener('openMyCompany', open);
  }, []);

  const openNotifications = () => {
    setNotifOpen(true);
    // Do NOT auto-mark everything read on open — that made the notifications come back already
    // read, so the "Mark all read" button never had anything to mark and stayed hidden. Reading
    // is now an explicit action via that button in the drawer. (#78778)
    clearNewNotificationFlag();
  };

  const closeNotifications = () => {
    setNotifOpen(false);
    clearNewNotificationFlag();
    loadNotificationCount();
  };

  return (
    <Flex overflow='hidden' w='100%' h='100dvh' bg='bg.canvas'>
      {/* Desktop sidebar */}
      <Box
        flexShrink={0}
        display={{ base: 'none', lg: 'flex' }}
        w={collapsed ? 'sidebarCollapsed' : 'sidebar'}
        transition='width 0.22s cubic-bezier(0.22, 1, 0.36, 1)'
      >
        <Sidebar
          groups={groups}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onOpenCompany={isAdmin ? () => setCompanyOpen(true) : undefined}
        />
      </Box>

      {/* Mobile sidebar drawer */}
      <Drawer isOpen={mobileOpen} onClose={() => setMobileOpen(false)} placement='left' size='xs'>
        <DrawerOverlay bg='rgba(16, 18, 24, 0.4)' />
        {/* 100dvh (not the default 100vh): on mobile browsers 100vh sits behind the address/
            toolbar, pushing the pinned user menu + Log out below the visible area. (#member-logout) */}
        <DrawerContent maxW='264px' h='100dvh' bg={drawerBg}>
          <Sidebar
            groups={mobileGroups}
            collapsed={false}
            insideDrawer
            onToggleCollapse={() => undefined}
            onNavigate={() => setMobileOpen(false)}
            onOpenCompany={isAdmin ? () => setCompanyOpen(true) : undefined}
          />
        </DrawerContent>
      </Drawer>

      {/* Main column */}
      <Flex direction='column' flex='1' minW={0} h='100%'>
        <Topbar
          title={title}
          crumb={crumb}
          unread={notificationCount.unread}
          onOpenNotifications={openNotifications}
          onOpenMobileNav={() => setMobileOpen(true)}
          onOpenCompany={isAdmin ? () => setCompanyOpen(true) : undefined}
        />
        {/* Definite-height flex region so screen roots can flex-fill with h='100%' + minH={0}
            instead of magic calc(100vh - Npx). Reaches exactly to the viewport bottom,
            aligned with the sidebar. (Bug A height-chain fix) */}
        <Flex as='main' direction='column' flex='1' overflowY='auto' minH={0}>
          <LoadingLayout isLoading={isLoading}>{children}</LoadingLayout>
        </Flex>
      </Flex>

      {/* Reused legacy drawers/modals — behavior unchanged */}
      <Notification isOpen={notifOpen} onClose={closeNotifications} />
      {isAdmin && <User isOpen={companyOpen} onClose={() => setCompanyOpen(false)} />}
    </Flex>
  );
};

export default AppShell;
